import React,{ useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { get, debounce } from 'lodash';
import * as echarts from 'echarts/lib/echarts';
import 'echarts/lib/chart/custom';
import 'echarts/lib/component/dataZoom';
import { ChartEvent } from '@components/common/Charts/BaseChart/BaseChart.typed';
import {
  timelineDataSelector,
} from '@store/timeline/data/selectors';
import timelineConfigSelector from '@store/timeline/config/selectors';
import { changeZoomLevel } from '@store/timeline/config/actions';
import { changeTimelineSizeCap } from '@store/timeline/request/actions';
import { TIMELINE_DIMENSION } from '@components/session-timeline/utils/constants';
import { StyledBaseChart } from '../../styles/timelineEventsStyled';
import {
  useTimelineOption,
  useEventListener,
  activateChartZoom,
} from '../../hooks';
import generateOption from './generateOption';
import { calculateGranularity } from './chartEvents';
import { StyledOverview } from './styles';

const SessionOverview = ({ dateRange }: { dateRange: number[] }) => {
  const dispatch = useDispatch();
  const debouncedDispatch = debounce(dispatch, 300);
  const [chartWidth, setChartWidth] = React.useState(0);
  const container = useRef<HTMLDivElement>(null);
  const [chartInstance, setInstance] = React.useState<echarts.ECharts | null>(
    null,
  );
  const [range, setRange] = useState<number>(0);

  const { startPoint, endPoint } = useSelector(timelineConfigSelector);
  const originalPoints = useState({ startPoint, endPoint })[0];

  const sessionStateData = get(
    useSelector(timelineDataSelector),
    [TIMELINE_DIMENSION.SessionState],
    [],
  );
  const sessionEventData = get(
    useSelector(timelineDataSelector),
    [TIMELINE_DIMENSION.SessionOverviewEvent],
    [],
  );

  useEffect(() => {
    if (container.current && dateRange.length) {
      const ele = container.current;
      const instance = echarts.getInstanceByDom(ele);

      // Get real width of chart by minus width of grid
      const width = instance.getWidth() - 240;

      // Divide chartWidth by the amount of elements on timeline to
      // get that how many widths one granularity can indicate(eg: 1 minute equal to 10px on screen)
      const res = Math.ceil(width / dateRange.length);

      setRange(res);
      setInstance(instance);
      setChartWidth(width);
    }
  }, [dateRange.length, container]);

  // if user change the width of browser, need to redraw the timeline
  const resizeHandler = React.useCallback(() => {
    if (chartInstance) {
      const newWidth = chartInstance.getWidth();
      const res = Math.floor(newWidth / dateRange.length);
      setRange(res);
    }
  }, [chartInstance, dateRange.length]);

  // subscribe resize event for window and change the range value for chart
  useEventListener('resize', resizeHandler);

  const option = useTimelineOption(
    generateOption,
    {
      range,
      stateData: sessionStateData,
      eventData: sessionEventData,
      dateRange,
      chartWidth,
    },
    [range, dateRange, chartWidth],
  );

  activateChartZoom(chartInstance, dateRange, originalPoints);

  const events: ChartEvent[] = React.useMemo(() => [
    [
      'datazoom',
      () => {
        if (!chartInstance) {
          return;
        }
        const {
          startValue,
          endValue,
          start,
          end,
        } = chartInstance.getOption().dataZoom[0];

        const newGranularity = calculateGranularity(startValue, endValue);
        debouncedDispatch(
          changeZoomLevel({
            startTime: startValue,
            endTime: endValue,
            startPoint: start,
            endPoint: end,
            granularity: newGranularity,
          }),
        );
      },
    ],
  ], [chartInstance, debouncedDispatch]);

  const handleChangeTimelineSizeCapAsFalse = React.useCallback(() => {
    dispatch(changeTimelineSizeCap(false));
  }, [dispatch]);

  const handleChangeTimelineSizeCapAsTrue = React.useCallback(() => {
    dispatch(changeTimelineSizeCap(true));
  }, [dispatch]);

  return (
    <StyledOverview>
      {/* <StyledLeftButton
        type="dashed"
        shape="circle"
        icon="left"
        onClick={handleChangeTimelineSizeCapAsFalse}
        disabled={timelineSizeCap === 0}
        size="small"
      /> */}
      <StyledBaseChart ref={container} option={option} events={events} renderer="svg"/>
      {/* <StyledRightButton
        type="dashed"
        shape="circle"
        size="small"
        icon="right"
        onClick={handleChangeTimelineSizeCapAsTrue}
      /> */}
    </StyledOverview>
  );
};

export default SessionOverview;
